import { NoticesComponentModule } from '@b2b/common/components/notices';
import { createDetailListOptions } from '@b2b/common/directives/detailList';
import { Notice, NoticeFactory } from '@b2b/common/factories/notice';
import { parseUrl } from '@b2b/common/filters';
import { CartService, CartServiceModule, ModalService, ModalServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { ContentHeaderService, ContentHeaderServiceModule } from '@b2b/common/services/contentHeader';
import { FeatureService, FeatureServiceModule } from '@b2b/common/services/feature/feature.service';
import {
  MembershipCardholderService,
  MembershipCardholderServiceModule,
} from '@b2b/common/services/membership/cardholder';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ACMEResponse, ColumnDef, ContentHeaderProps, TODO } from '@b2b/types/core';
import {
  extractEmbeddedAddressFromCardholder,
  isPrimaryCardholder,
  isSecondaryCardholder,
  MembershipCardholder,
  MembershipDetails,
} from '@b2b/types/membership';
import { SubscriptionDetail } from '@b2b/types/subscriptions';
import { getFullName } from '@b2b/common/services/helpers';
import uiRouter, { StateParams, StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IRootScopeService, IScope, ITimeoutService } from 'angular';
import moment from 'moment-timezone';
import invariant from 'tiny-invariant';
import { MembershipDetailsVM } from '../../controllers/types';
import { MembershipDetailService, MembershipDetailServiceModule } from '../../services/membership-detail.service';

interface DropdownOption {
  label: string;
  permissions?: string[];
  action: () => void;
}

MembershipDetailMemberController.$inject = [
  '$scope',
  '$rootScope',
  '$state',
  '$stateParams',
  '$timeout',
  '$filter',
  'ApiService',
  'CartService',
  'HelpersService',
  'CalendarService',
  'MembershipService',
  'ThemesHelperService',
  'DatePickerNoteService',
  'UserPreferencesService',
  'PermissionsService',
  'FeatureService',
  'FeatureToggleService',
  'MembershipData',
  'SubscriptionData',
  'ContentHeaderService',
  'MembershipDetailService',
  'MembershipCardholderService',
  'ModalService',
  'NoticeFactory',
];

function MembershipDetailMemberController(
  $scope: IScope & { membership: TODO; membershipDetailMember: TODO },
  $rootScope: IRootScopeService,
  $state: StateService,
  $stateParams: StateParams,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  apiService: ApiService,
  cartService: CartService,
  HelpersService: TODO,
  CalendarService: TODO,
  MembershipService: TODO,
  ThemesHelperService: TODO,
  DatePickerNoteService: TODO,
  UserPreferencesService: TODO,
  permissionsService: PermissionsService,
  featureService: FeatureService,
  FeatureToggleService: TODO,
  MembershipData: { data: ACMEResponse<MembershipDetails> },
  SubscriptionData: { data: ACMEResponse<SubscriptionDetail> },
  contentHeaderService: ContentHeaderService,
  membershipDetailService: MembershipDetailService,
  membershipCardholderService: MembershipCardholderService,
  modalService: ModalService,
  NoticeFactory: NoticeFactory,
) {
  const ctrl = {
    notice: new NoticeFactory() as Notice,
    subscriptionDetailsData: null as SubscriptionDetail | null,

    ctaDropdownOptions: [] as DropdownOption[],

    model: {} as MembershipCardholder,
    errors: {} as Record<string, TODO>,

    membership: {} as MembershipDetailsVM,
    primaryCardholder: null as MembershipCardholder | null,

    expirationDate: '' as Date | string,
    originalExpirationDate: '' as Date | string,
    membershipExpirationDate: '' as Date | string,
    displayMembershipExpirationDate: '',
    isSameExpiration: false,
    isLoaded: false,
    minDate: '' as Date | string,
    maxDate: '' as Date | string,
    revertExpiration: false,
    skipExpirationDateUpdateOnce: false,

    formatDate: (parsedDate: TODO) => {
      return $filter('date')(parsedDate, UserPreferencesService.getDateFormat('standardDate', 'dateFormat'));
    },

    memberDetails: createDetailListOptions({
      columns: 2,
      columnWidth: 4,
      data: 'membershipDetailMember.model',
      include: [
        'cardType',
        'cardStatus',
        'primaryCard',
        'isGift',
        'acmeMembershipNumber',
        'acmeCustomerNumber',
        'acmeBarcode',
        'primaryConstituentId',
        'constituentId',
        'constituentImportId',
        'cardImportId',
        'barcode',
        'startDate',
        'joinedDate',
        'expirationDate',
        'deceased',
        'printStatus',
        'printCount',
      ],
      mappings: {
        primaryConstituentId: 'External Customer Id',
        membershipId: 'ACME Membership Id',
        acmeCustomerNumber: 'ACME Customer Number',
        barcode: 'External Barcode',
      },
      filters: {
        acmeCustomerNumber: (acmeCustomerNumber: string, row: MembershipCardholder) => {
          if (featureService.isEnabled(featureService.customers) && row.customerId) {
            const href = $state.href('application.customers.individuals.detail', { id: row.customerId });
            return parseUrl(href, acmeCustomerNumber);
          }

          return acmeCustomerNumber;
        },
        cardStatus: (value) => HelpersService.cap(value),
        startDate: (value) => ctrl.formatDate(CalendarService.parseDate(value)),
        joinedDate: (value) => ctrl.formatDate(CalendarService.parseDate(value)),
        expirationDate: (value) => ctrl.formatDate(CalendarService.parseDate(value)),
        isGift: (value) => (value ? 'Yes' : 'No'),
      },
      templates: {
        expirationDate: {
          getScope: $scope,
          permissions: 'MEMBERSHIP_UPDATE',
          template: `
            <span data-ng-controller="MembershipDetailDatePickerController" class="inputs input-group membership-detail-edit-field">
              <input id="date-picker-membership-detail-member" type="text"
                     name="date-picker-membership-detail-member"
                     class="form-control"
                     data-ng-show="membershipDetailDatePicker.isPreferenceLoaded"
                     data-uib-datepicker-popup="{{ membershipDetailDatePicker.getDateFormat() }}"
                     data-datepicker-append-to-body="true"
                     data-datepicker-options="membershipDetailDatePicker.dateOptions"
                     data-datepicker-popup-template-url="common/includes/datePickerPopup.tpl.html"
                     data-ng-model="membershipDetailMember.expirationDate"
                     data-ng-required="required"
                     data-ng-disabled="membershipDetailMember.isReadOnly()"
                     data-ng-click="membershipDetailMember.openExpirationPicker($event);membershipDetailDatePicker.open($event);"
                     data-ng-blur="membershipDetailMember.checkDateExists()"
                     data-ng-disabled="membershipDetailMember.isCanceled()"
                     data-show-button-bar="false"
                     data-min-date="membershipDetailMember.minDate"
                     data-max-date="membershipDetailMember.maxDate"
                     data-is-open="membershipDetailDatePicker.isOpen"
                     data-close-text="Close" />
              <span class="date-btn input-group-btn">
                <button type="button" class="btn btn-default" data-ng-disabled="membershipDetailMember.isCanceled()" data-ng-click="membershipDetailMember.openExpirationPicker($event);membershipDetailDatePicker.open($event);" aria-label="To calendar"><i class="glyphicon glyphicon-calendar"></i></button>
              </span>
            </span>

            <div class="membership-detail-member-reset" data-ng-if="!membershipDetailMember.isCanceled()">
              <em data-ng-show="membershipDetailMember.isSameExpiration" class="note">Same as membership expiration</em>
              <a data-ng-hide="membershipDetailMember.isSameExpiration" data-ng-click="membershipDetailMember.resetExpirationDate()" class="font-small">Use membership expiration: {{ membershipDetailMember.displayMembershipExpirationDate }}</a>
            </div>

            <em class="font-small note" data-ng-if="membershipDetailMember.isCanceled()"><span class="fa fa-warning" role="presentation"></span> Rejoin to update expiration</em>
          `,
        },
      },
    }),

    contactDetails: createDetailListOptions({
      columns: 1,
      columnWidth: 4,
      data: 'membershipDetailMember.model',
      include: [
        'prefix',
        'name',
        'orgName',
        'email',
        'phoneNumber',
        'streetAddress1',
        'streetAddress2',
        'city',
        'state',
        'country',
        'zipCode',
      ],
      mappings: {
        name: 'Name on Card',
        streetAddress1: 'Street Address 1',
        streetAddress2: 'Street Address 2',
      },
    }),

    attributeGridOptions: {
      showFooter: true,
      multiSelect: false,
      paginate: true,
      pageSize: 50,
      columnDefs: [
        {
          field: 'category',
          displayName: 'Category',
          sort: {
            direction: 'asc',
          },
        },
        {
          field: 'description',
          displayName: 'Description',
        },
        {
          field: 'notes',
          displayName: 'Notes',
        },
        {
          field: 'date',
          displayName: 'Date',
          cellFilter: 'date: "' + HelpersService.dateFormats.sortDate + '"',
          cellFormatter: function (item) {
            return $filter('date')(item.value, HelpersService.dateFormats.shortDateTime);
          },
        },
      ] as ColumnDef[],
    },

    contentTitle: {
      title: 'Membership Cardholder',
      buttonLeft: {
        label: '< Back',
        action: function () {
          $state.go(
            'application.membership.detail',
            { id: $stateParams.id, searchName: $stateParams.searchName },
            { reload: true },
          );
        },
      },
    } as ContentHeaderProps,

    init: function () {
      contentHeaderService.update(ctrl.contentTitle);

      ctrl.subscriptionDetailsData = SubscriptionData.data?.success ? SubscriptionData.data.payload : null;

      if (MembershipData.data && MembershipData.data.success) {
        ctrl.success.init(MembershipData);
      } else {
        ctrl.error.init(MembershipData);
      }

      $scope.$on('$destroy', $scope.$watch('membershipDetailMember.expirationDate', ctrl.onExpirationDateUpdate));
    },

    getCardStatusDropdownOptions: function (): DropdownOption[] {
      if (isPrimaryCardholder(ctrl.model)) {
        return [];
      }

      switch (ctrl.model.cardStatus) {
        case 'new':
        case 'active':
          return [
            {
              label: 'Deactivate',
              action: () => {
                ctrl.deactivateMemberPrompt();
              },
            },
          ];

        case 'dropped':
          return [
            {
              label: 'Activate',
              action: () => {
                ctrl.activateMemberPrompt();
              },
            },
          ];

        default:
          return [];
      }
    },

    getCtaDropdownOptions: function () {
      const deleteOptions: DropdownOption[] = !ctrl.isArchived()
        ? [
          {
            label: 'Delete',
            permissions: ['MEMBERSHIP_CARD_DELETE'],
            action: () => ctrl.deleteCardholderPrompt(),
          },
        ]
        : [];

      const options: DropdownOption[] = [...ctrl.getCardStatusDropdownOptions(), ...deleteOptions];

      return options.filter((option) => permissionsService.every(option.permissions ?? []));
    },

    resetExpirationDate: function () {
      ctrl.expirationDate =
        ctrl.membershipExpirationDate instanceof Date
          ? new Date(ctrl.membershipExpirationDate.getTime())
          : ctrl.membershipExpirationDate;
    },

    setIsSameExpiration: function () {
      if (ctrl.expirationDate == null) {
        ctrl.resetExpirationDate();
      }

      const e = ctrl.expirationDate;
      const m = ctrl.membershipExpirationDate;
      const expirationDate = e instanceof Date ? new Date(e.getFullYear(), e.getMonth(), e.getDate()) : e;
      const membershipExpirationDate = m instanceof Date ? new Date(m.getFullYear(), m.getMonth(), m.getDate()) : m;

      ctrl.isSameExpiration =
        CalendarService.toISO8601(expirationDate) === CalendarService.toISO8601(membershipExpirationDate);
    },

    openExpirationPicker: function (e) {
      $rootScope.$broadcast('MembershipDetailDatePickerController::DatePickerNote', {
        id: 'membership-detail-expiration-range',
        note:
          'Expiration date must fall between term, ' +
          ctrl.formatDate(ctrl.minDate) +
          ' - ' +
          ctrl.formatDate(ctrl.membershipExpirationDate),
      });
    },

    getCustomer: function () {
      let result: { customerId: TODO; cardholder?: TODO } = { customerId: null };

      if (ctrl.model.customerId) {
        result = {
          customerId: ctrl.model.customerId,
          cardholder: ctrl.model,
        };
      } else if (ctrl.membership.customerId) {
        result = {
          customerId: ctrl.membership.customerId,
          cardholder: ctrl.membership,
        };
      }
      return result;
    },

    transactAsMember: function () {
      /**
       * TODO: The membership used to purchase this order item could be different from the membershipId of the item itself.
       *       Especially when auxiliary memberships come into play.
       */
      /**
       * Use current cardholder for renewal order contact otherwise use primary
       */
      ctrl.updateOrderContact(() => {
        ctrl.goToState('application.programs.list');
      });
    },

    deleteCardholder: function () {
      const cardholderId = ctrl.model.id;
      const membershipId = ctrl.model.membershipId;

      invariant(cardholderId);
      invariant(membershipId);

      return apiService.membership
        .deleteCardholder({ id: cardholderId, membershipId: membershipId })
        .then(() => {
          $state.go(
            'application.membership.detail',
            { id: ctrl.model.membershipId },
            { location: 'replace', reload: true },
          );
        })
        .catch((response) => {
          ctrl.showMessage({ title: 'An error occurred', message: response.data.error });
        });
    },

    activateMember: function () {
      const data = angular.copy(ctrl.membership);
      const index = ctrl.findCurrentMember(data).index;
      data.cardholders[index].cardStatus = 'active';

      return apiService.membership.update(data).then(() => {
        ctrl.skipExpirationDateUpdateOnce = true;
        ctrl.reload();
      });
    },

    deactivateMember: function () {
      const data = angular.copy(ctrl.membership);
      const index = ctrl.findCurrentMember(data).index;
      data.cardholders[index].cardStatus = 'dropped';

      return apiService.membership.update(data).then(() => {
        ctrl.skipExpirationDateUpdateOnce = true;
        ctrl.reload();
      });
    },

    activateMemberPrompt: function () {
      ctrl
        .showMessage({
          title: 'Are you sure?',
          message: 'Are you sure you want to activate?',
          okText: 'Continue',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          return ctrl.activateMember();
        });
    },

    deactivateMemberPrompt: function () {
      ctrl
        .showMessage({
          title: 'Are you sure?',
          message: 'Are you sure you want to deactivate?',
          okText: 'Continue',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          return ctrl.deactivateMember();
        });
    },

    deleteCardholderPrompt: function () {
      ctrl
        .showMessage({
          title: 'Are you sure?',
          message: 'Do you want to delete this cardholder?',
          okText: 'Continue',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          return ctrl.deleteCardholder();
        });
    },

    canEditCardholder: () => {
      if (!permissionsService.every(['MEMBERSHIP_UPDATE', 'MEMBERSHIP_CARD_UPDATE'])) {
        return false;
      }

      return ctrl.membership.membershipStanding === 'active' && ctrl.model.cardStatus === 'active';
    },

    editCardholder: function () {
      const isAutorenewMembership = membershipDetailService.isActiveSubscription(ctrl.subscriptionDetailsData);
      const membership = angular.copy(ctrl.membership);
      const originalCardholder = angular.copy(ctrl.model);

      membershipCardholderService
        .showCardholderModal({
          mode: 'edit',
          cardholder: angular.copy(originalCardholder),
          isAutorenewMembership,
          membershipAddresses: {
            id: membership.id,
            addresses: membership.cardholders?.map(extractEmbeddedAddressFromCardholder) ?? [],
          },
        })
        .result.then((response) => {
          const cardholder = response.contact;

          const index = membership.cardholders.findIndex((item) => item.id === cardholder.id);
          membership.cardholders[index] = cardholder;

          if (isPrimaryCardholder(cardholder) && isSecondaryCardholder(originalCardholder)) {
            return apiService.membership
              .update(membershipDetailService.createSwappedPrimaryCardholderModel(membership, cardholder))
              .then(() => {
                membershipCardholderService.showPrimaryCardholderChangedMessage(ctrl.notice);
                ctrl.reload();
              });
          }

          return apiService.membership.update(membership).then(() => {
            ctrl.reload();
          });
        })
        .catch((response: string | { data: { error: string } }) => {
          if (typeof response === 'string') {
            return;
          }

          ctrl.showMessage({
            title: 'An error occurred',
            message: response.data.error,
          });
        });
    },

    reload: function () {
      return apiService.membership
        .detail(ctrl.membership.id)
        .then((response) => {
          ctrl.success.init(response);
        })
        .catch((response) => {
          ctrl.error.init(response);
        });
    },

    renewMembership: function (options: { showOptions: boolean }) {
      /**
       * TODO: The membership used to purchase this order item could be different from the membershipId of the item itself.
       *       Especially when auxiliary memberships come into play.
       */
      /**
       * Use current cardholder for renewal order contact otherwise use primary
       */
      const membership: {
        quantity: TODO;
        itemType: TODO;
        membershipId: TODO;
        orderContactId: TODO;
        categoryId: TODO;
        membershipCategory: TODO;
        offeringId: TODO;
        offeringName: TODO;
        pricePointId: TODO;
        amount?: TODO;
      } = {
        quantity: 1,
        itemType: MembershipService.getPurchaseType('renew'),
        membershipId: ctrl.model.membershipId,
        orderContactId: ctrl.getCustomer().customerId,
        categoryId: ctrl.membership.categoryId,
        membershipCategory: ctrl.membership.membershipCategory,
        offeringId: ctrl.membership.offeringId,
        offeringName: ctrl.membership.offeringName,
        pricePointId: ctrl.membership.pricePointId,
      };

      cartService.memberships
        .lifecycle(cartService.memberships.ENUMS.renew, {
          membershipId: membership.membershipId,
          membershipCategory: membership.membershipCategory,
          membershipName: ctrl.model['primaryCardholder'].name,
        })
        .then((response) => {
          const found = cartService.memberships.hasPricepointMatch(membership, response.data.payload);

          if (!options.showOptions && found) {
            membership.amount = found.pricepoint.price;
            membership.itemType = MembershipService.getPurchaseType(found.type || 'renew');
            ctrl.updateOrderContact(() => {
              cartService.memberships.addToCart(membership).then(ctrl.success.addToCart).catch(ctrl.error.addToCart);
            });
          } else {
            ctrl.updateOrderContact(() => {
              ctrl.goToState('application.membership.retail');
            });
          }
        })
        .catch(ctrl.error.renewMembership);
    },

    updateOrderContact: function (callBack: () => void) {
      const customer = ctrl.getCustomer();
      const showPrompt = cartService.hasOrderContact() && !cartService.isOrderContact(customer.customerId);

      if (!showPrompt) {
        ctrl.updateContactAndProceed(callBack);
        return;
      }

      const contactName = getFullName(cartService.getOrderContact());
      const cardholderName = getFullName(customer.cardholder);

      ctrl
        .showMessage({
          title: 'Are you sure?',
          message: customer.customerId
            ? `Do you want to transact as ${cardholderName} instead of ${contactName}?<br><br>Doing so will remove any memberships in the cart.`
            : `Do you want to change the transacting member from ${contactName}?<br><br>Doing so will remove any memberships in the cart.`,
          okText: 'Continue',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          ctrl.updateContactAndProceed(callBack);
        });
    },

    updateContactAndProceed: function (callBack) {
      cartService.memberships
        .updateOrderContact({
          membershipId: ctrl.model.membershipId,
          cardholders: ctrl.membership.cardholders,
          orderContactId: ctrl.getCustomer().customerId,
        })
        .then(callBack)
        .catch(ctrl.error.renewMembership);
    },

    goToState: function (state) {
      const props: { membershipId: TODO; orderContactId?: TODO } = {
        membershipId: ctrl.model.membershipId,
      };
      const customer = ctrl.getCustomer();

      if (customer.customerId) {
        props.orderContactId = customer.customerId;
      }

      $state.go(state, props);
    },

    checkDateExists: function () {
      if (!angular.isDate(ctrl.expirationDate)) {
        ctrl.revertExpiration = true;
        ctrl.expirationDate = ctrl.originalExpirationDate;
        $timeout(() => {
          ctrl.revertExpiration = false;
        }, 0);
      }
    },

    updateCardholderExpiration: function () {
      const d = ctrl.expirationDate;
      const data = angular.copy(ctrl.membership);

      if (angular.isDate(d)) {
        const date = d as Date;
        const targetTz = moment
          .tz([date.getFullYear(), date.getMonth(), date.getDate()], ThemesHelperService.getCompany('timezone'))
          .format('ZZ');
        const expDate = ctrl.isSameExpiration
          ? ctrl.membershipExpirationDate instanceof Date
            ? new Date(ctrl.membershipExpirationDate.getTime())
            : ctrl.membershipExpirationDate
          : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

        const cardholder = data.cardholders.find((item) => item.id === ctrl.model.id);
        if (cardholder) {
          cardholder.expirationDate = CalendarService.toTZISO8601(expDate, targetTz) as string;
          const currentDate = new Date(CalendarService.toTZISO8601(new Date(), targetTz));
          const expirationDate = new Date(cardholder.expirationDate);
          cardholder.cardStatus = expirationDate.getTime() > currentDate.getTime() ? 'active' : 'lapsed';
        }

        apiService.membership
          .update(data)
          .then(ctrl.success.updateCardholderExpiration)
          .catch(ctrl.error.updateCardholderExpiration);
      }
    },

    findCurrentMember: function (data: MembershipDetails) {
      return membershipDetailService.getCardholderByCardImportId(data, $state.params.memberId);
    },

    process: function (data: MembershipDetails) {
      const member = ctrl.findCurrentMember(data).member;

      if (isPrimaryCardholder(member)) {
        ctrl.primaryCardholder = member;

        if (member.hasOwnProperty('constituentId')) {
          member['primaryConstituentId'] = member.constituentId;
        }
      }

      return member;
    },

    showMessage: function (props: { title: string; message: string; okText?: string }) {
      if (!props.message) {
        return Promise.resolve(false);
      }

      return modalService.confirm(props);
    },

    isReadOnly: () => {
      return ctrl.isCanceled() || ctrl.isArchived();
    },

    isCanceled: () => {
      return ctrl.membership.membershipStanding?.toLowerCase() === 'dropped';
    },

    isArchived: () => {
      return ctrl.membership.membershipStanding?.toLocaleLowerCase() === 'archived';
    },

    success: {
      init: function (response: { data: ACMEResponse<MembershipDetails> }) {
        const membership = response.data.payload;

        try {
          ctrl.model = ctrl.process(membership);
        } catch (error) {
          /**
           * Handles the case in which the user deletes a cardholder, they get redirected
           * to the memberships list and then they press back on their browser.
           */
          $state.go('application.membership.list');
          return;
        }

        ctrl.membership = membership as MembershipDetailsVM;
        ctrl.expirationDate = CalendarService.parseDate(ctrl.model.expirationDate);
        ctrl.originalExpirationDate = ctrl.expirationDate;
        ctrl.membershipExpirationDate = CalendarService.parseDate(ctrl.membership.membershipExpirationDate);
        ctrl.minDate = CalendarService.parseDate(ctrl.membership.membershipStartDate);
        ctrl.maxDate = ctrl.membershipExpirationDate;
        ctrl.displayMembershipExpirationDate = ctrl.formatDate(ctrl.membershipExpirationDate);
        ctrl.setIsSameExpiration();
        ctrl.ctaDropdownOptions = ctrl.getCtaDropdownOptions();

        ctrl.isLoaded = true;

        if (
          FeatureToggleService.check('TENANT_CART') &&
          permissionsService.check(['ORDER_CREATE', 'ACCT_ORDERS_CREATE']) &&
          ctrl.membership.membershipStanding &&
          ctrl.membership.membershipStanding !== 'new'
        ) {
          ctrl.contentTitle.buttonRight = !ctrl.isArchived()
            ? {
              label: 'Transact',
              action: () => {
                ctrl.transactAsMember();
              },
              items: permissionsService.check('MEMBERSHIP_SALES')
                ? [
                  {
                    label: 'Renew',
                    action: () => {
                      ctrl.renewMembership({ showOptions: false });
                    },
                  },
                  {
                    label: 'Renew Options',
                    action: () => {
                      ctrl.renewMembership({ showOptions: true });
                    },
                  },
                ]
                : undefined,
            }
            : undefined;

          contentHeaderService.update(ctrl.contentTitle);
        }
      },

      updateCardholderExpiration: function (response) {
        const membership = response.data.payload;

        ctrl.revertExpiration = true;

        for (let i = 0, ii = membership.cardholders.length; i < ii; i++) {
          const cardholder = membership.cardholders[i];

          if (cardholder.id === ctrl.model.id) {
            ctrl.model.cardStatus = cardholder.cardStatus;
            ctrl.expirationDate = CalendarService.parseDate((ctrl.model.expirationDate = cardholder.expirationDate));
            ctrl.originalExpirationDate = ctrl.expirationDate;
            ctrl.membershipExpirationDate = CalendarService.parseDate(
              (ctrl.membership.membershipExpirationDate = membership.membershipExpirationDate),
            );
            ctrl.displayMembershipExpirationDate = ctrl.formatDate(ctrl.membershipExpirationDate);
            ctrl.setIsSameExpiration();
            ctrl.isLoaded = true;
            break;
          }
        }

        $timeout(() => {
          ctrl.revertExpiration = false;
        });
      },
      addToCart: function (response) {
        // Do nothing
        $scope.$emit('Cart::Open');
      },
    },

    error: {
      init: function (response) {
        ctrl.isLoaded = true;
      },
      updateCardholderExpiration: function (response) {
        const callback = function () {
          ctrl.revertExpiration = false;
        };

        ctrl.revertExpiration = true;
        ctrl.expirationDate = CalendarService.parseDate(ctrl.model.expirationDate);
        ctrl.setIsSameExpiration();
        $scope.$broadcast('MembershipDetailDatePickerController::Close');
        /**
         * Intentional use of scope chain access
         */
        $scope.membership.showMessage('Invalid Date Selected', response.data.error, null, callback, callback);
      },
      renewMembership: function (response) {
        ctrl.showMessage({ title: 'An error occurred', message: response.data.error });
      },
      addToCart: function (response) {
        ctrl.showMessage({ title: 'An error occurred', message: response.data.error });
      },
    },

    checkDateEquality: function (firstDate: Date | string, secondDate: Date | string) {
      return moment(firstDate).isSame(secondDate);
    },

    onExpirationDateUpdate: function (newValue: Date | string, oldValue: Date | string) {
      if (newValue && oldValue && !ctrl.checkDateEquality(newValue, oldValue)) {
        ctrl.setIsSameExpiration();

        /**
         * We need this here when updating the `cardStatus` to activate/deactivate the cardholder because
         * this watcher gets triggered and overrides the change with a new membership update.
         * @link https://acmeticketing.atlassian.net/browse/APP-2278
         */
        if (ctrl.skipExpirationDateUpdateOnce) {
          ctrl.skipExpirationDateUpdateOnce = false;
          return;
        }

        if (!ctrl.revertExpiration) {
          ctrl.updateCardholderExpiration();
        }
      }
    },
  };

  $scope.membershipDetailMember = ctrl;
  ctrl.init();
}

export const MembershipDetailMemberControllerModule = angular
  .module('acme.view.application.membership.detail.member.controllers', [
    uiRouter,
    'acme.common.service.api',
    CartServiceModule,
    'acme.common.service.helpers',
    'acme.common.service.calendar',
    'acme.common.service.membership',
    'acme.common.service.themesHelper',
    'acme.common.service.userPreferences',
    'acme.common.service.datePickerNote',
    'acme.common.service.permissions',
    FeatureServiceModule,
    'acme.common.service.featureToggle',
    'acme.common.factory.notice',
    ContentHeaderServiceModule,
    MembershipDetailServiceModule,
    MembershipCardholderServiceModule,
    ModalServiceModule,
    NoticesComponentModule,
    'acme.view.application.modals.confirm.controllers',
    'acme.view.application.membership.detail.datePicker.controllers',
  ])
  .controller('MembershipDetailMemberController', MembershipDetailMemberController).name;
